import { Box, Input, List, ListItem } from "@chakra-ui/react";
import { fromPlaceId, toMfAddressComponents } from "mf-google-geocoder";
import React from "react";
import usePlacesService from "react-google-autocomplete/lib/usePlacesAutocompleteService";
import { Messages } from "../../../core/api";
import { GOOGLE_MAPS_API_KEY } from "../../../shared/consts";

interface Props {
  addressText: string;
  isDisabled?: boolean;
  value: Messages["AddressComponents"] | null;
  onChange: (address: string, addressComponents: Messages["AddressComponents"]) => void;
  onClear: () => void;
}

const AddressAutocompleteInput = (props: Props) => {
  const [input, setInput] = React.useState(props.addressText);
  const [showPredictions, setShowPredictions] = React.useState(false);

  const { placesService, placePredictions, getPlacePredictions } = usePlacesService({
    apiKey: GOOGLE_MAPS_API_KEY,
    debounce: 250,
    language: "en",
  });

  React.useEffect(() => {
    setInput(props.addressText);
  }, [props.addressText]);

  const getDetails = (
    prediction: google.maps.places.AutocompletePrediction
  ): Promise<google.maps.places.PlaceResult | null> => {
    return new Promise((resolve, reject) => {
      placesService?.getDetails({ placeId: prediction.place_id }, (placeResult, status) => {
        if (
          status === google.maps.places.PlacesServiceStatus.OK ||
          status === google.maps.places.PlacesServiceStatus.ZERO_RESULTS
        ) {
          resolve(placeResult);
        } else {
          reject(status);
        }
      });
    });
  };

  const handleClickPrediction = async (prediction: google.maps.places.AutocompletePrediction) => {
    setShowPredictions(false);
    try {
      const result = await getDetails(prediction);
      const placeId = result?.place_id;
      if (placeId === undefined) return;

      const addressDetails = await fromPlaceId(placeId, {
        apiKey: GOOGLE_MAPS_API_KEY,
        mfAutoFix: true,
      });
      const components = toMfAddressComponents(addressDetails);
      setInput(prediction.description);
      props.onChange(prediction.description, components);
    } catch (error) {
      console.error("Error getting address details", error);
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setInput(value);
    setShowPredictions(value !== "");
    getPlacePredictions({ input: value });
    props.onClear();
  };

  return (
    <Box position="relative">
      <Input
        autoComplete="new-password"
        isDisabled={props.isDisabled}
        placeholder="Search address..."
        value={input}
        onChange={handleInputChange}
      />
      {showPredictions && placePredictions.length > 0 && (
        <Box
          bg="white"
          border="1px"
          borderColor="chakra-border-color"
          padding={2}
          position="absolute"
          width="100%"
          zIndex={10}
        >
          <List maxHeight="200px" overflowY="auto">
            {placePredictions.map((prediction) => (
              <ListItem
                key={prediction.place_id}
                _hover={{ bg: "gray.50" }}
                borderBottom="1px"
                borderColor="chakra-border-color"
                cursor="pointer"
                px={2}
                py={2}
                onClick={() => handleClickPrediction(prediction)}
              >
                {prediction.description}
              </ListItem>
            ))}
          </List>
        </Box>
      )}
    </Box>
  );
};

export default AddressAutocompleteInput;
